import { visibleAlertsFilters } from '@/atoms/alerts_filters'
import { AlertTableColumnLabels } from '@/generated/enums'
import { Order_By } from '@/generated/graphql'
import { useSelectedPage } from '@/hooks/spoof_report_table_filters'
import { useAppSettings } from '@/utils/app_settings'
import {
  DOPPEL_DARK_CLICKABLE_HOVER,
  DOPPEL_WHITE,
  DOPPEL_TEXT_GREY,
  DOPPEL_FONT_FAMILY_BODY,
  FONT_SIZE_MEDIUM,
} from '@/utils/style'
import { Box, Flex, MenuButton } from '@chakra-ui/react'
import { useSetAtom } from 'jotai'
import { Dropdown } from '../doppel_design/dropdown'
import { FaArrowUp, FaArrowDown } from 'react-icons/fa'
import { ReportFilterType } from '@/utils/constants'
import { SortType } from '@/utils/reports/report_sorting_mechanism'

type HeaderMenuProps = {
  id: string
  sortType: SortType
  filterType: ReportFilterType
  disableColumnHiding?: boolean
  sortingMechanism?: {
    sortingType: SortType
    orderBy: Order_By
  }
  onSortingChange?: (sortingMechanism: {
    sortingType: SortType
    orderBy: Order_By
  }) => void
}

export const HeaderMenu = ({
  id,
  sortType,
  filterType,
  disableColumnHiding = false,
  sortingMechanism = null,
  onSortingChange = null,
}: HeaderMenuProps) => {
  const {
    data: appSettings,
    loading: appSettingsLoading,
    update: updateAppSettings,
  } = useAppSettings()
  const [selectedPage, setSelectedPage] = useSelectedPage()
  const setSelectedVisibleAlertsFilters = useSetAtom(visibleAlertsFilters)

  const isActive = sortingMechanism?.sortingType === sortType

  const handleSortingChange = (orderBy: Order_By) => {
    if (onSortingChange) {
      onSortingChange({
        sortingType: sortType,
        orderBy,
      })
      setSelectedPage(1)
    }
  }

  const handleHideColumn = () => {
    if (appSettingsLoading || disableColumnHiding) return

    updateAppSettings({
      alert_table_column_override: {
        ...appSettings?.alert_table_column_override,
        [id]: false,
      },
    })
  }

  const isSortingActive = isActive

  const displaySortIcon = (sortType) => {
    if (!sortType || !sortingMechanism) return <></>

    if (sortingMechanism.sortingType === sortType) {
      if (sortingMechanism.orderBy === Order_By.AscNullsLast) {
        return (
          <Box paddingX="0.5">
            <FaArrowUp size="12" />
          </Box>
        )
      } else {
        return (
          <Box paddingX="0.5">
            <FaArrowDown size="12" />
          </Box>
        )
      }
    } else {
      return <></>
    }
  }

  const getSelectedValue = (sortType) => {
    if (!sortType || !sortingMechanism) return ''

    if (sortingMechanism.sortingType === sortType) {
      if (sortingMechanism.orderBy === Order_By.AscNullsLast) {
        return sortType + '_' + Order_By.AscNullsLast
      } else {
        return sortType + '_' + Order_By.DescNullsLast
      }
    } else {
      return ''
    }
  }

  // Build menu items array
  const menuItems = [
    // Sorting options
    ...(sortType && sortingMechanism && onSortingChange
      ? [
          {
            label: 'Sort Ascending',
            value: sortType + '_' + Order_By.AscNullsLast,
            action: () => {
              handleSortingChange(Order_By.AscNullsLast)
            },
          },
          {
            label: 'Sort Descending',
            value: sortType + '_' + Order_By.DescNullsLast,
            action: () => {
              handleSortingChange(Order_By.DescNullsLast)
            },
          },
        ]
      : []),

    // Hide column option - only show if column hiding is not disabled
    ...(!disableColumnHiding
      ? [
          {
            label: 'Hide Column',
            value: `hide_${id}`,
            action: () => {
              handleHideColumn()
            },
          },
        ]
      : []),
  ]

  // Check if there are any menu items to show
  const hasMenuItems = menuItems.length > 0

  return (
    <Box borderBottom="none" marginY={2}>
      {!hasMenuItems ? (
        <Box
          fontFamily={DOPPEL_FONT_FAMILY_BODY}
          fontSize={FONT_SIZE_MEDIUM}
          fontWeight="normal"
          letterSpacing="normal"
          textTransform="capitalize"
          whiteSpace="nowrap"
        >
          {AlertTableColumnLabels[id] || id}
        </Box>
      ) : (
        <Dropdown
          customMenuButton={
            <MenuButton
              _hover={{ bgColor: DOPPEL_DARK_CLICKABLE_HOVER, borderRadius: 'md' }}
              alignItems="center"
              as={Box}
              color={isSortingActive ? DOPPEL_WHITE : DOPPEL_TEXT_GREY}
              cursor="pointer"
              display="flex"
            >
              <Flex align="center" gap={1.5}>
                <Box
                  fontFamily={DOPPEL_FONT_FAMILY_BODY}
                  fontSize={FONT_SIZE_MEDIUM}
                  fontWeight="normal"
                  letterSpacing="normal"
                  textTransform="capitalize"
                  whiteSpace="nowrap"
                >
                  {AlertTableColumnLabels[id] || id}
                </Box>

                {displaySortIcon(sortType)}
              </Flex>
            </MenuButton>
          }
          items={menuItems}
          onSelect={(value) => menuItems.find((item) => item.value === value)?.action()}
          selectedValue={getSelectedValue(sortType)}
        />
      )}
    </Box>
  )
}
